import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc,
  writeBatch,
  setDoc
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

/**
 * One-time migration script to add userId to existing documents
 * Run this once when the first user is logged in
 */
export async function migrateToMultiTenancy() {
  const user = auth.currentUser;
  if (!user) {
    console.error('No user logged in. Cannot migrate.');
    return;
  }

  console.log(`Starting migration for user: ${user.email} (${user.uid})`);

  try {
    // Migrate portfolios
    console.log('Migrating portfolios...');
    const portfoliosSnapshot = await getDocs(collection(db, 'portfolios'));
    const portfolioBatch = writeBatch(db);
    let portfolioCount = 0;

    portfoliosSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      if (!data.userId) {
        portfolioBatch.update(doc(db, 'portfolios', docSnapshot.id), {
          userId: user.uid
        });
        portfolioCount++;
      }
    });

    if (portfolioCount > 0) {
      await portfolioBatch.commit();
      console.log(`Migrated ${portfolioCount} portfolios`);
    } else {
      console.log('No portfolios to migrate');
    }

    // Migrate watchlist entries
    console.log('Migrating watchlist entries...');
    const watchlistSnapshot = await getDocs(collection(db, 'watchlist'));
    const watchlistBatch = writeBatch(db);
    let watchlistCount = 0;

    watchlistSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      if (!data.userId) {
        // Update the document with userId
        watchlistBatch.update(doc(db, 'watchlist', docSnapshot.id), {
          userId: user.uid
        });
        watchlistCount++;
      }
    });

    if (watchlistCount > 0) {
      await watchlistBatch.commit();
      console.log(`Migrated ${watchlistCount} watchlist entries`);
    } else {
      console.log('No watchlist entries to migrate');
    }

    // Migrate trading rules
    console.log('Migrating trading rules...');
    const rulesSnapshot = await getDocs(collection(db, 'tradingRules'));
    const rulesBatch = writeBatch(db);
    let rulesCount = 0;

    rulesSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      if (!data.userId && !data.isGlobal) {
        rulesBatch.update(doc(db, 'tradingRules', docSnapshot.id), {
          userId: user.uid
        });
        rulesCount++;
      }
    });

    if (rulesCount > 0) {
      await rulesBatch.commit();
      console.log(`Migrated ${rulesCount} trading rules`);
    } else {
      console.log('No trading rules to migrate');
    }

    // Migrate watchlist snapshots
    console.log('Migrating watchlist snapshots...');
    const snapshotsSnapshot = await getDocs(collection(db, 'watchlistSnapshots'));
    const snapshotsBatch = writeBatch(db);
    let snapshotsCount = 0;

    snapshotsSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      if (!data.userId) {
        snapshotsBatch.update(doc(db, 'watchlistSnapshots', docSnapshot.id), {
          userId: user.uid
        });
        snapshotsCount++;
      }
    });

    if (snapshotsCount > 0) {
      await snapshotsBatch.commit();
      console.log(`Migrated ${snapshotsCount} watchlist snapshots`);
    } else {
      console.log('No snapshots to migrate');
    }

    // Migrate API usage records
    console.log('Migrating API usage records...');
    const apiUsageSnapshot = await getDocs(collection(db, 'apiUsage'));
    const apiUsageBatch = writeBatch(db);
    let apiUsageCount = 0;

    apiUsageSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      if (!data.userId) {
        apiUsageBatch.update(doc(db, 'apiUsage', docSnapshot.id), {
          userId: user.uid
        });
        apiUsageCount++;
      }
    });

    if (apiUsageCount > 0) {
      await apiUsageBatch.commit();
      console.log(`Migrated ${apiUsageCount} API usage records`);
    } else {
      console.log('No API usage records to migrate');
    }

    console.log('Migration completed successfully!');
    
    // Store migration status
    await updateDoc(doc(db, 'metadata', 'migration_status'), {
      multiTenancyMigrated: true,
      migratedAt: new Date(),
      migratedBy: user.uid
    }).catch(() => {
      // Create if doesn't exist
      return setDoc(doc(db, 'metadata', 'migration_status'), {
        multiTenancyMigrated: true,
        migratedAt: new Date(),
        migratedBy: user.uid
      });
    });

  } catch (error) {
    console.error('Migration failed:', error);
    throw error;
  }
}

// Make it available in the console for one-time execution
if (typeof window !== 'undefined') {
  (window as any).migrateToMultiTenancy = migrateToMultiTenancy;
}